import { t } from 'i18next';
import { urlHelper } from 'src/app/helpers';
import { apiSlice } from 'src/app/redux/api/apiSlice';
import {
  TransactionIdentifiedCreateDataType,
  TransactionIdentifiedDataType,
  TransactionIdentifiedLookupDataType,
  TransactionIdentifiedLookupResultType,
  TransactionIdentifiedUpdateDataType,
  TransactionRecognizedLookupResultType,
} from 'src/app/redux/transactionOfInterests/transactions-slice.type';
import { finalUrlsConfig } from 'src/configs/url.config';

export const transactionIdentifiedSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTransactionsIdentified: builder.query<
      TransactionIdentifiedLookupResultType,
      TransactionIdentifiedLookupDataType | void
    >({
      query: (params?: TransactionIdentifiedLookupDataType) => ({
        url: finalUrlsConfig().interest.identified.transaction.lookup,
        params: urlHelper.getLookupQuery(params),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.rows.map(({ id }) => ({ type: 'TransactionsIdentified' as const, id })),
              { type: 'TransactionsIdentified', id: 'LIST' },
            ]
          : [{ type: 'TransactionsIdentified', id: 'LIST' }],
    }),

    createTransactionIdentified: builder.mutation<TransactionIdentifiedDataType, TransactionIdentifiedCreateDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.transaction.create,
        body: data,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'TransactionsIdentified', id: 'LIST' }],
    }),
    updateTransactionIdentified: builder.mutation<TransactionIdentifiedDataType, TransactionIdentifiedUpdateDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.transaction.update,
        body: data,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, data) => [{ type: 'TransactionsIdentified', id: data.id }],
    }),
    deleteTransactionIdentified: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `${finalUrlsConfig().interest.identified.transaction.delete}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'TransactionsIdentified', id }],
    }),
    exportTransactionIdentifiedExcell: builder.mutation<ArrayBuffer, { ids?: string[] }>({
      query: (data) => ({
        url: finalUrlsConfig().exports.transaction,
        method: 'POST',
        body: data,
        responseHandler: async (response: any) => {
          if (!response.ok) {
            const errorResponse = await response.json();
            throw new Error(errorResponse?.message || t('errors.defaultErrorText'));
          }
          return response.blob();
        },
        cache: 'no-cache',
      }),
    }),
    getTransactionsRecognized: builder.query<TransactionRecognizedLookupResultType, void>({
      query: () => ({
        url: finalUrlsConfig().interest.recognized.transaction.lookup,
      }),
    }),
  }),
});

export const {
  useGetTransactionsIdentifiedQuery,
  useLazyGetTransactionsIdentifiedQuery,
  useCreateTransactionIdentifiedMutation,
  useUpdateTransactionIdentifiedMutation,
  useDeleteTransactionIdentifiedMutation,
  useExportTransactionIdentifiedExcellMutation,
  useGetTransactionsRecognizedQuery,
} = transactionIdentifiedSlice;
